import {
  canonicalizeDisplayName,
  normalizeHeader,
  normalizeLooseName,
  normalizeName,
  toNumber,
} from "./inventoryImport.util";

export {
  canonicalizeDisplayName,
  extractApiErrorMessage,
  normalizeLooseName,
  normalizeName,
  toNumber,
} from "./inventoryImport.util";

export function buildServiceHeaderMap(headers) {
  const map = {};
  (headers || []).forEach((h) => {
    const key = normalizeHeader(h);
    if (!key) return;

    if (["service name", "servicename", "name", "service"].includes(key)) {
      map.serviceName = h;
    }
    if (["category", "category name", "categoryname"].includes(key)) {
      map.category = h;
    }
    if (["duration", "time", "mins", "minutes"].includes(key)) {
      map.duration = h;
    }
    if (
      ["amount", "price", "rate", "service price", "service amount"].includes(
        key
      )
    ) {
      map.amount = h;
    }
    if (["colour", "color", "service colour", "service color"].includes(key)) {
      map.colour = h;
    }
    if (["sac", "sac code", "saccode"].includes(key)) {
      map.sacCode = h;
    }
  });
  return map;
}

export function validateServiceRow(row, headerMap) {
  const errors = [];
  const get = (field) => row?.[headerMap?.[field]];

  const serviceName = String(get("serviceName") ?? "").trim();
  const category = String(get("category") ?? "").trim();
  const durationRaw = get("duration");
  const amountRaw = get("amount");
  const duration =
    durationRaw === "" || durationRaw === undefined || durationRaw === null
      ? null
      : toNumber(durationRaw);
  const amount =
    amountRaw === "" || amountRaw === undefined || amountRaw === null
      ? null
      : toNumber(amountRaw);
  const colour = String(get("colour") ?? "").trim();
  const sacCode = String(get("sacCode") ?? "").trim();

  if (!serviceName) errors.push("Service Name is required");
  if (!category) errors.push("Category is required");
  if (durationRaw !== "" && durationRaw != null && !Number.isFinite(duration)) {
    errors.push("Duration must be a number");
  }
  if (amountRaw !== "" && amountRaw != null && !Number.isFinite(amount)) {
    errors.push("Amount must be a number");
  }
  if (Number.isFinite(duration) && duration < 0) {
    errors.push("Duration must be >= 0");
  }
  if (Number.isFinite(amount) && amount < 0) {
    errors.push("Amount must be >= 0");
  }

  return {
    ok: errors.length === 0,
    errors,
    parsed: {
      serviceName,
      category,
      duration: Number.isFinite(duration) ? duration : undefined,
      amount: Number.isFinite(amount) ? amount : undefined,
      colour,
      sacCode,
    },
  };
}

const DEFAULT_COLOURS = [
  "#1479FF",
  "#E66666",
  "#27AE60",
  "#F39C12",
  "#8E44AD",
  "#16A085",
  "#2980B9",
];

export function pickDefaultCategoryColor(seed = "") {
  const s = String(seed);
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash + s.charCodeAt(i) * 17) % 997;
  return DEFAULT_COLOURS[hash % DEFAULT_COLOURS.length];
}

/**
 * Classify service Excel rows before write.
 */
export function classifyServiceRows({
  rows,
  headerMap,
  autoCreateMissing,
  existingServiceByName,
  categoryMap,
}) {
  const planRows = [];
  const categoriesToCreate = new Map();
  const seenInFile = new Set();
  let toCreate = 0;
  let toUpdate = 0;
  let errors = 0;

  const resolveCategory = (name) => {
    const keyStrict = normalizeName(name);
    const keyLoose = normalizeLooseName(name);
    return categoryMap?.[keyStrict] || categoryMap?.[keyLoose] || null;
  };

  (rows || []).forEach((row, i) => {
    const excelRowNumber = i + 2;
    const { ok, errors: rowErrors, parsed } = validateServiceRow(row, headerMap);
    if (!ok) {
      errors += 1;
      planRows.push({
        excelRowNumber,
        action: "error",
        error: rowErrors.join(", "),
        serviceName: parsed?.serviceName || "",
        category: parsed?.category || "",
        parsed: null,
        existingId: null,
      });
      return;
    }

    const nameKey = normalizeName(parsed.serviceName);
    if (nameKey && seenInFile.has(nameKey)) {
      errors += 1;
      planRows.push({
        excelRowNumber,
        action: "error",
        error: `Duplicate in Excel file: ${parsed.serviceName}`,
        serviceName: parsed.serviceName,
        category: parsed.category,
        parsed,
        existingId: null,
      });
      return;
    }
    if (nameKey) seenInFile.add(nameKey);

    const categoryExisting = resolveCategory(parsed.category);
    if (!categoryExisting) {
      if (autoCreateMissing) {
        const k =
          normalizeLooseName(parsed.category) || normalizeName(parsed.category);
        if (k) {
          categoriesToCreate.set(k, {
            categoryName: canonicalizeDisplayName(parsed.category),
            categoryColor: pickDefaultCategoryColor(parsed.category),
          });
        }
      } else {
        errors += 1;
        planRows.push({
          excelRowNumber,
          action: "error",
          error: `Category not found: ${parsed.category}`,
          serviceName: parsed.serviceName,
          category: parsed.category,
          parsed,
          existingId: null,
        });
        return;
      }
    }

    const existing = nameKey ? existingServiceByName?.[nameKey] : null;
    if (existing?.id) {
      toUpdate += 1;
      planRows.push({
        excelRowNumber,
        action: "update",
        error: null,
        serviceName: parsed.serviceName,
        category: parsed.category,
        parsed,
        existingId: existing.id,
      });
    } else {
      toCreate += 1;
      planRows.push({
        excelRowNumber,
        action: "create",
        error: null,
        serviceName: parsed.serviceName,
        category: parsed.category,
        parsed,
        existingId: null,
      });
    }
  });

  return {
    summary: {
      toCreate,
      toUpdate,
      errors,
      categoriesToCreate: categoriesToCreate.size,
      totalRows: (rows || []).length,
    },
    planRows,
    categoriesToCreate: Array.from(categoriesToCreate.values()),
  };
}
